import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import useIsDesktop from "@/hooks/useIsDesktop";
import { useRouter } from "next/router";
import Cookies from "js-cookie";
import dayjs from "dayjs";
import { map, orderBy } from "lodash";
import { IoCart, IoChevronDown } from "react-icons/io5";
import { FaCheck, FaTimes, FaShoppingCart } from "react-icons/fa";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { createCart, createTempBooking } from "@/services/api/cart";
import { getCurrencySymbol } from "@/utils/getCurrencySymbol";
import { formatIndianPrice } from "@/utils/formatIndianPrice";
import { isIndianUser } from "@/utils/getCountryCode";
import { useAuth } from "@/Common/Auth/AuthContext";
import { getAuthToken, getGuestId, withRegionPath } from "@/utils/session";
import { decryptNumberAES } from "@/utils/Encryption";
import { scrollToSectionElement } from "@/utils/scrollToSection";
import {
  buildCartRequestPayload,
  buildModuleRoom,
} from "@/utils/bookingHelpers";
import styles from "./ActivitySummary.module.css";

function resolveCoupon(raw) {
  if (!raw) return "";
  try {
    const decrypted = decryptNumberAES(raw);
    return decrypted || raw;
  } catch {
    return raw;
  }
}

function getOptionsType(data) {
  if (Array.isArray(data?.optionsType) && data.optionsType.length) {
    return data.optionsType;
  }
  return (data?.options || []).map((opt) => ({
    optionId: opt.optionId,
    optionName: opt.optionName || "",
    optionsTypeName: opt.optionsTypeName || "",
    optionsTypeId: opt.optionsTypeId,
    isMultipleTimeSlot:
      opt.isMultipleTimeSlot === true || opt.isMultipleTimeSlot === 1 ? 1 : 0,
    duration: opt.duration || "",
    startTime: opt.startTime || "",
  }));
}

function getBookingTimeslot(data) {
  if (Array.isArray(data?.bookingTimeslot) && data.bookingTimeslot.length) {
    return data.bookingTimeslot;
  }
  return (data?.options || []).flatMap((opt) =>
    (opt.timeslots || []).map((slot) => ({
      ...slot,
      optionId: opt.optionId,
      startTime: slot.startTime || opt.startTime || "",
    }))
  );
}

function getOperatingHours(data) {
  if (Array.isArray(data?.operatingHours) && data.operatingHours.length) {
    return data.operatingHours;
  }
  return (data?.options || []).flatMap((opt) =>
    (opt.operatingHours || []).map((hour) => ({
      ...hour,
      optionId: hour.optionId || opt.optionId,
    }))
  );
}

function getTransfers(data) {
  const raw = data?.transfer || data?.transfers || [];
  return Array.isArray(raw) ? raw : [];
}

function getBoxInSideBar(data, activity) {
  if (data?.boxInSideBar) return data.boxInSideBar;
  const meta = activity?.pricingMeta || {};
  const tags = activity?.tags || {};
  return {
    price: meta.fromPrice,
    displayCutPrice: meta.displayCutPrice,
    cutPricePercent: meta.cutPricePercent,
    cutPriceAmount: meta.cutPriceAmount,
    discountPercent: meta.cutPricePercent,
    discountTag: tags.discountTag || tags.tag2 || "",
  };
}

function getTruncatedHTML(html = "", wordLimit = 70) {
  const text = String(html)
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length <= wordLimit) {
    return { text, isTruncated: false };
  }
  return { text: words.slice(0, wordLimit).join(" "), isTruncated: true };
}

function calculateOriginalPrice(finalPrice, discountPercent, discountAmount) {
  if (discountPercent && discountPercent > 0) {
    return finalPrice / (1 - discountPercent / 100);
  }
  if (discountAmount && discountAmount > 0) {
    return finalPrice + discountAmount;
  }
  return finalPrice;
}

export default function ActivitySummary({
  data,
  selectPackageRef,
  hideIncludesOnMobile = false,
}) {
  const router = useRouter();
  const { openAuthModal } = useAuth();
  const activityData = data || {};
  const activities = activityData.activity || activityData.activities || {};
  const bookingRules = activities.bookingRules || {};
  const boxInSideBar = getBoxInSideBar(activityData, activities);

  const renderCurrency = (variant = "main", size = 14) => {
    if (activities.isSpecialActivity) return "USD ";
    return getCurrencySymbol(variant, size);
  };

  const [activeTab, setActiveTab] = useState(0);
  const [travelDate, setTravelDate] = useState(null);
  const [isOpen, setIsOpen] = useState(true);
  const [packageType, setPackageType] = useState("standard");
  const [isBookingAvailable, setIsBookingAvailable] = useState(true);
  const [bookingMessage, setBookingMessage] = useState("");
  const [showTravelerDropdown, setShowTravelerDropdown] = useState(false);
  const [selectedTransferForPackage, setSelectedTransferForPackage] = useState(
    {}
  );
  const [selectedTime, setSelectedTime] = useState("");
  const [bookingLoading, setBookingLoading] = useState(false);
  const [cartLoading, setCartLoading] = useState(false);
  const [bookingIndex, setBookingIndex] = useState(null);
  const actionLockRef = useRef(false);
  const [showEnquiryForm, setShowEnquiryForm] = useState(false);
  const [showFullHighlights, setShowFullHighlights] = useState(false);
  const [highlightsOpen, setHighlightsOpen] = useState(true);
  const [inclusionOpen, setInclusionOpen] = useState(true);
  const [showInclusions, setShowInclusions] = useState(true);
  const [showExclusions, setShowExclusions] = useState(true);
  const [selectedTravelers, setSelectedTravelers] = useState({
    adults: 1,
    children: 0,
    infants: 0,
  });
  const [unlockFree, setUnlockFree] = useState(false);
  const [show, setShow] = useState(false);

  const userId = parseInt(Cookies.get("user_id") || "0", 10) || 0;
  const inclusionExclusionRef = useRef(null);
  const highlightsRef = useRef(null);
  const localSelectPackageRef = useRef(null);
  const selectPackageRefResolved = selectPackageRef || localSelectPackageRef;
  const tabSentinelRef = useRef(null);
  const tabsRef = useRef(null);

  const { isDesktop } = useIsDesktop(900);
  const [tabsPinned, setTabsPinned] = useState(false);
  const [tabsHeight, setTabsHeight] = useState(0);

  const navbarOffset = isDesktop ? 76 : 72;

  const optionsType = getOptionsType(activityData);
  const activityOptions = activityData.pricing || [];
  const transfers = getTransfers(activityData);
  const bookingTimeslot = getBookingTimeslot(activityData);
  const operatingHours = getOperatingHours(activityData);

  const getPackagesByType = (type) => {
    const typeIds = optionsType
      .filter(
        (opt) =>
          String(opt.optionsTypeName || "").toLowerCase() ===
          String(type).toLowerCase()
      )
      .map((opt) => opt.optionId);

    return activityOptions.filter(
      (option) =>
        option.type === "Option" && typeIds.includes(option.optionId)
    );
  };

  const standardOptions = getPackagesByType("Standard");
  const comboOptions = getPackagesByType("Combo Deals");

  useEffect(() => {
    setTravelDate(dayjs().add(1, "day").toDate());
  }, []);

  useEffect(() => {
    if (isDesktop) {
      setTabsPinned(false);
      return undefined;
    }

    const tabsEl = tabsRef.current;
    const sentinelEl = tabSentinelRef.current;
    if (!tabsEl || !sentinelEl) return undefined;

    const measureTabs = () => {
      setTabsHeight(tabsEl.offsetHeight);
    };

    const onScroll = () => {
      const { top } = sentinelEl.getBoundingClientRect();
      setTabsPinned(top <= navbarOffset);
    };

    measureTabs();
    onScroll();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measureTabs)
        : null;
    resizeObserver?.observe(tabsEl);

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", measureTabs);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", measureTabs);
    };
  }, [isDesktop, navbarOffset, activityData]);

  useEffect(() => {
    if (isOpen) {
      setIsOpen(false);
      const timer = setTimeout(() => setIsOpen(true), 100);
      return () => clearTimeout(timer);
    }
  }, [packageType]);

  useEffect(() => {
    if (activities.isAnniversaryActivity) {
      if (
        selectedTravelers.adults === 2 &&
        selectedTravelers.children === 0 &&
        selectedTravelers.infants === 0
      ) {
        setUnlockFree(true);
        setShowTravelerDropdown(false);
      } else {
        setUnlockFree(false);
      }
    }
  }, [selectedTravelers, activities.isAnniversaryActivity]);

  useEffect(() => {
    if (!unlockFree) return;
    setShow(true);
    const timer = setTimeout(() => setShow(false), 3000);
    return () => clearTimeout(timer);
  }, [unlockFree]);

  useEffect(() => {
    if (comboOptions.length === 0) setPackageType("standard");
  }, [comboOptions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".traveler-dropdown-container")) {
        setShowTravelerDropdown(false);
      }
    };
    if (showTravelerDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showTravelerDropdown]);

  useEffect(() => {
    if (!transfers.length) return;
    const defaultTransfers = {};
    const withoutTransferOption = transfers.find((transfer) =>
      String(transfer.transferName || "")
        .toLowerCase()
        .includes("without transfer")
    );

    [...standardOptions, ...comboOptions].forEach((pkg) => {
      const packageKey = pkg.sellingId || pkg.optionId || "default";
      if (!selectedTransferForPackage[packageKey]) {
        defaultTransfers[packageKey] = withoutTransferOption
          ? String(withoutTransferOption.transferId)
          : String(transfers[0].transferId);
      }
    });

    if (Object.keys(defaultTransfers).length > 0) {
      setSelectedTransferForPackage((prev) => ({ ...prev, ...defaultTransfers }));
    }
  }, [transfers, standardOptions, comboOptions]);

  const includesData = activityData.includes || [];
  const items = [];
  if (includesData.length > 0) {
    includesData.forEach((include) => {
      if (include.includesTypeName && include.includesImagePath) {
        items.push({
          label: include.includesTypeName,
          icon: include.includesImagePath,
        });
      }
    });
  }

  const highlightsDesc =
    activityData.activitiesDescriptions?.[0]?.highlightsDescription ||
    activityData.activitiesDescriptions?.[0]?.highlightsDesc ||
    activityData.descriptions?.highlightsDescription ||
    activityData.descriptions?.highlightsDesc ||
    "";

  const inclusionExclusionList = Array.isArray(activityData.inclusionExclusion)
    ? activityData.inclusionExclusion
    : [];
  const inclusions = inclusionExclusionList.length
    ? inclusionExclusionList.map((x) => x.inclusionName).filter(Boolean)
    : (activityData.inclusions || []).filter(Boolean);
  const exclusions = inclusionExclusionList.length
    ? inclusionExclusionList.map((x) => x.exclusionName).filter(Boolean)
    : (activityData.exclusions || []).filter(Boolean);

  const handleRouteRegion = (path) => {
    try {
      return withRegionPath(path);
    } catch {
      return isIndianUser() ? `/in${path}` : path;
    }
  };

  const getTimeSlots = (optionId) => {
    const optionType = optionsType.find((opt) => opt.optionId === optionId);
    const bookingSlot = bookingTimeslot.find(
      (slot) => slot.optionId === optionId
    );

    if (!optionType || !bookingSlot || !travelDate) return [];

    const dayNames = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    const selectedDay = dayNames[dayjs(travelDate).day()];

    if (!bookingSlot[selectedDay]) return [];

    const operatingHour = operatingHours.find(
      (hour) =>
        hour.optionId === optionId &&
        String(hour.daysName || "").toLowerCase() === selectedDay &&
        hour.isClosed === false
    );

    if (!operatingHour) return [];

    if (optionType.isMultipleTimeSlot === 1) {
      return [
        {
          startTime: bookingSlot.startTime,
          available: bookingSlot[selectedDay],
        },
      ];
    }

    const openTime = operatingHour.openTime;
    const closeTime = operatingHour.closeTime;
    const timeSlots = [];
    let start = new Date(`2000-01-01T${openTime}`);
    let end = new Date(`2000-01-01T${closeTime}`);

    if (end < start) {
      end = new Date(`2000-01-02T${closeTime}`);
    }

    while (start < end) {
      timeSlots.push({
        startTime: start.toTimeString().slice(0, 8),
        closeTime,
        available: true,
      });
      start.setMinutes(start.getMinutes() + 15);
    }

    return timeSlots;
  };

  const getFilteredPackagesByAvailability = (options) => {
    if (!travelDate) return options;

    const dayNames = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    const selectedDay = dayNames[dayjs(travelDate).day()];

    return options.filter((option) => {
      const timeslot = bookingTimeslot.find(
        (slot) => slot.optionId === option.optionId
      );

      if (!timeslot || !timeslot[selectedDay]) return false;
      if (!selectedTime) return true;

      const timeSlots = getTimeSlots(option.optionId);
      return timeSlots.some(
        (slot) =>
          slot.startTime === selectedTime ||
          slot.startTime.slice(0, 5) === selectedTime.slice(0, 5)
      );
    });
  };

  const checkBookingAvailability = (
    selectedDate,
    selectedTimeSlot = selectedTime
  ) => {
    if (!selectedDate) {
      setIsBookingAvailable(true);
      setBookingMessage("");
      return;
    }

    const dayNames = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    const selectedDay =
      dayNames[
      selectedDate instanceof Date
        ? selectedDate.getDay()
        : dayjs(selectedDate).day()
      ];

    const currentOptions =
      packageType === "standard" ? standardOptions : comboOptions;

    if (currentOptions.length === 0) {
      setIsBookingAvailable(false);
      return;
    }

    const availableOnDay = currentOptions.filter((option) => {
      const timeslot = bookingTimeslot.find(
        (slot) => slot.optionId === option.optionId
      );
      return timeslot && timeslot[selectedDay];
    });

    if (selectedTimeSlot) {
      const availableAtTime = availableOnDay.filter((option) => {
        const timeSlots = getTimeSlots(option.optionId);
        return timeSlots.some(
          (slot) =>
            slot.startTime === selectedTimeSlot ||
            slot.startTime.slice(0, 5) === selectedTimeSlot.slice(0, 5)
        );
      });

      if (availableAtTime.length === 0) {
        setIsBookingAvailable(false);
        setBookingMessage(
          `Activity is not available on ${selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1)}s at ${String(selectedTimeSlot).slice(0, 5)}. Please select a different time.`
        );
        return;
      }
    }

    setIsBookingAvailable(true);
    setBookingMessage("");
  };

  const handleTravelerChange = (type, action) => {
    setSelectedTravelers((prev) => {
      const newCount =
        action === "increment" ? prev[type] + 1 : Math.max(0, prev[type] - 1);
      if (type === "adults" && newCount < 1) return prev;
      return { ...prev, [type]: newCount };
    });
  };

  const calculateTotalPrice = (packageOption, packageSellingId = null) => {
    if (!packageOption) return 0;

    const totalMembers =
      selectedTravelers.adults +
      selectedTravelers.children +
      selectedTravelers.infants;

    let totalPrice = 0;
    const packageKey =
      packageSellingId && packageSellingId !== 0
        ? packageSellingId
        : packageOption.optionId;
    const selectedTransferId = selectedTransferForPackage[packageKey];

    const withoutTransferOption = transfers.find(
      (transfer) =>
        transfer.transferName &&
        transfer.transferName.toLowerCase().includes("without transfer")
    );

    const baseActivityOption = activityOptions.find(
      (option) =>
        option.type === "Option" && option.optionId === packageOption.optionId
    );

    if (baseActivityOption) {
      totalPrice +=
        selectedTravelers.adults * (baseActivityOption.finalAdultPrice || 0);
      totalPrice +=
        selectedTravelers.children * (baseActivityOption.finalChildPrice || 0);
    }

    if (
      selectedTransferId &&
      withoutTransferOption &&
      selectedTransferId == withoutTransferOption.transferId
    ) {
      return totalPrice;
    }

    if (
      selectedTransferId &&
      selectedTransferId != withoutTransferOption?.transferId
    ) {
      const transferOptions = activityOptions.filter(
        (option) =>
          option.type === "Transfer" && option.transferId == selectedTransferId
      );

      if (transferOptions.length > 0) {
        const transferOption = transferOptions[0];

        if (transferOption.fromPax === 0 && transferOption.toPax === 0) {
          totalPrice +=
            selectedTravelers.adults * (transferOption.finalAdultPrice || 0) +
            selectedTravelers.children * (transferOption.finalChildPrice || 0);
        } else {
          const sortedTransferOptions = [...transferOptions].sort(
            (a, b) => b.toPax - a.toPax
          );
          let remainingPax = totalMembers;
          let transferTotalPrice = 0;

          while (remainingPax > 0) {
            const applicableTransfer = sortedTransferOptions.find(
              (transfer) =>
                remainingPax >= transfer.fromPax &&
                remainingPax <= transfer.toPax
            );

            if (!applicableTransfer) {
              const largestTransfer = sortedTransferOptions.find(
                (transfer) =>
                  transfer.toPax > 0 && remainingPax >= transfer.fromPax
              );
              if (largestTransfer) {
                transferTotalPrice += largestTransfer.finalAdultPrice || 0;
                remainingPax -= largestTransfer.toPax;
              } else {
                break;
              }
            } else {
              transferTotalPrice += applicableTransfer.finalAdultPrice || 0;
              remainingPax -= applicableTransfer.toPax;
            }
          }

          totalPrice += transferTotalPrice;
        }
      }
    }

    return totalPrice;
  };

  const handleTransferChange = (packageSellingId, transferId) => {
    const packageKey =
      packageSellingId && packageSellingId !== 0 ? packageSellingId : "default";
    setSelectedTransferForPackage((prev) => ({
      ...prev,
      [packageKey]: transferId,
    }));
  };

  const getTravelerDisplayText = () => {
    let text = `${selectedTravelers.adults} Adult${selectedTravelers.adults > 1 ? "s" : ""
      }`;

    if (bookingRules.childNotAllowed && selectedTravelers.children > 0) {
      text += `, ${selectedTravelers.children} Child${selectedTravelers.children > 1 ? "ren" : ""
        }`;
    }

    if (bookingRules.infantNotAllowed && selectedTravelers.infants > 0) {
      text += `, ${selectedTravelers.infants} Infant${selectedTravelers.infants > 1 ? "s" : ""
        }`;
    }

    return text;
  };

  const isBookingFormValid = () => {
    if (!travelDate) {
      return { valid: false, message: "Please select a travel date" };
    }
    if (selectedTravelers.adults < 1) {
      return {
        valid: false,
        message: "Please select at least 1 adult traveler",
      };
    }
    return { valid: true, message: "" };
  };

  const handleAddToCart = async (pkg, type = "cart") => {
    if (actionLockRef.current || cartLoading || bookingLoading) return;

    const validation = isBookingFormValid();
    if (!validation.valid) {
      alert(validation.message);
      return;
    }

    const token = getAuthToken();
    const tempUserId = token ? "" : getGuestId();

    if (!activityData?.pricing?.length) {
      alert("Pricing data is not available for this activity.");
      return;
    }

    if (!pkg) {
      alert("Please select a package.");
      return;
    }

    actionLockRef.current = true;
    if (type === "booking") setBookingLoading(true);
    else setCartLoading(true);

    const packageKey =
      pkg.sellingId && pkg.sellingId !== 0 ? pkg.sellingId : pkg.optionId;
    const selectedTransferId = selectedTransferForPackage[packageKey];
    const withoutTransferOption = transfers.find((transfer) =>
      String(transfer.transferName || "")
        .toLowerCase()
        .includes("without transfer")
    );

    const finalTransferId = selectedTransferId
      ? parseInt(selectedTransferId, 10)
      : withoutTransferOption
        ? withoutTransferOption.transferId
        : 0;

    const couponCode =
      typeof router.query.couponCode === "string"
        ? router.query.couponCode
        : "";

    const payload = buildCartRequestPayload({
      tempUserId,
      token: token || "",
      module: "Activity",
      travelDate: travelDate
        ? dayjs(travelDate).format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD"),
      moduleEntries: [
        {
          serviceId: activities.activityId || 0,
          transferId: finalTransferId,
          startTime: selectedTime
            ? `${String(selectedTime).slice(0, 5)}:00`
            : "09:00:00",
          slabId: pkg.slabId || 0,
          couponCode: couponCode ? resolveCoupon(couponCode) : "",
          rooms: [
            buildModuleRoom({
              optionId: pkg.optionId || 0,
              adult: selectedTravelers.adults || 0,
              child: selectedTravelers.children || 0,
              infant: selectedTravelers.infants || 0,
              ages: [
                ...Array.from({ length: selectedTravelers.children }, () => ({
                  age: 8,
                  type: "Child",
                })),
                ...Array.from({ length: selectedTravelers.infants }, () => ({
                  age: 1,
                  type: "Infant",
                })),
              ],
            }),
          ],
        },
      ],
    });

    if (type === "booking") {
      Cookies.set("id", "");
      Cookies.set("CartId", "");
      Cookies.set("module", "");
      try {
        const response = await createTempBooking(payload);
        if (String(response?.status || "").toLowerCase() !== "success") {
          alert(response?.message || "Failed to booking.");
          return;
        }
        Cookies.set("id", response.data);
        Cookies.set("module", "Activity");
        router.push(
          handleRouteRegion(
            couponCode
              ? `/activity/package-booking?couponCode=${encodeURIComponent(couponCode)}`
              : "/activity/package-booking"
          )
        );
      } catch (err) {
        alert(
          err?.response?.data?.message || "Something went wrong while booking."
        );
      } finally {
        actionLockRef.current = false;
        setBookingLoading(false);
      }
      return;
    }

    try {
      const response = await createCart(payload);
      if (String(response?.status || "").toLowerCase() !== "success") {
        alert("Failed to add to cart. Please try again.");
        return;
      }
      router.push(handleRouteRegion("/cart"));
    } catch (err) {
      alert(
        err?.response?.data?.message ||
        "Something went wrong. The item could not be added to the cart. Please try again."
      );
    } finally {
      actionLockRef.current = false;
      setCartLoading(false);
    }
  };

  const tabs = ["Highlights", "Select Packages", "Inclusion/Exclusion"];

  const scrollToSection = (index) => {
    setActiveTab(index);
    const refs = [
      highlightsRef,
      selectPackageRefResolved,
      inclusionExclusionRef,
    ];
    const el = refs[index]?.current;
    if (el) {
      scrollToSectionElement(el, { tabsRef, fallbackNavHeight: navbarOffset });
    }
  };

  const renderPackageOptions = (options, typeLabel) =>
    map(orderBy(options, "finalAdultPrice", "asc"), (pkg, index) => {
      const optionTypeData = optionsType.find(
        (opt) => Number(opt.optionId) === Number(pkg.optionId)
      );
      const packageSellingId = pkg.sellingId;
      const packageKey =
        packageSellingId && packageSellingId !== 0
          ? packageSellingId
          : pkg.optionId;

      const withoutTransferOption = transfers.find((transfer) =>
        String(transfer.transferName || "")
          .toLowerCase()
          .includes("without transfer")
      );

      const transferValue =
        selectedTransferForPackage[packageKey] ||
        (withoutTransferOption
          ? String(withoutTransferOption.transferId)
          : String(transfers[0]?.transferId || ""));

      const total = calculateTotalPrice(pkg, packageSellingId);
      const original = calculateOriginalPrice(
        total,
        boxInSideBar.cutPricePercent || 0,
        boxInSideBar.cutPriceAmount || 0
      );

      const getOperatingHoursForSelectedDate = () => {
        if (!travelDate) return "Select a date to view operating hours";

        const dayNames = [
          "sunday",
          "monday",
          "tuesday",
          "wednesday",
          "thursday",
          "friday",
          "saturday",
        ];
        const selectedDay = dayNames[dayjs(travelDate).day()];
        const operatingHour = operatingHours.find(
          (hour) =>
            hour.optionId === pkg.optionId &&
            String(hour.daysName || "").toLowerCase() === selectedDay
        );

        if (!operatingHour) return "Operating hours not available";
        if (operatingHour.isClosed) return "Closed on this day";
        return `${String(operatingHour.openTime).slice(0, 5)} - ${String(
          operatingHour.closeTime
        ).slice(0, 5)}`;
      };

      return (
        <div
          key={`${pkg.sellingId || pkg.optionId}-${index}`}
          className={styles.packageCard}
        >
          <div className={styles.packageNameCol}>
            <p className={styles.packageName}>
              {optionTypeData?.optionName || pkg.optionName}
            </p>
            <p className={styles.operatingHours}>
              Operating hours: {getOperatingHoursForSelectedDate()}
            </p>
          </div>
          <div className={styles.packageDetailCol}>
            <div className={styles.packageDetailColInner}>
              <div className={styles.packageDetails}>
                <h5 className={styles.detailsTitle}>
                  {typeLabel === "standard" ? "Package Details" : "Combo Details"}
                </h5>
                <ul className={styles.detailsList}>
                  <li>
                    <span
                      dangerouslySetInnerHTML={{
                        __html:
                          pkg.optionDesc ||
                          (typeLabel === "standard"
                            ? "Standard activity package with all inclusions"
                            : `${pkg.transferName || "Combo Deals"} with activity ticket`),
                      }}
                    />
                  </li>
                </ul>
              </div>

              <div className={styles.packageDetails}>
                <div className={styles.field}>
                  <label
                    className={styles.fieldLabel}
                    htmlFor={`transfer-${packageKey}`}
                  >
                    Select Transfer
                  </label>
                  <select
                    id={`transfer-${packageKey}`}
                    className={styles.selectInput}
                    value={transferValue}
                    onChange={(e) =>
                      handleTransferChange(packageKey, e.target.value)
                    }
                    disabled={!isBookingAvailable}
                  >
                    {transfers.length > 0 ? (
                      transfers.map((transfer) => (
                        <option key={transfer.transferId} value={transfer.transferId}>
                          {String(transfer.transferName || "").length > 17
                            ? `${transfer.transferName.slice(0, 17)}...`
                            : transfer.transferName}
                        </option>
                      ))
                    ) : (
                      <option value="">No transfers available</option>
                    )}
                  </select>
                  <IoChevronDown className={styles.inputSvg} />
                </div>
              </div>
              <div className={styles.packageDetails}>
                <div className={styles.priceBlock}>
                  <div className={styles.startsFromRow}>
                    <span className={styles.startsFrom}>Starts From</span>
                    {boxInSideBar.displayCutPrice > 0 ? (
                      <span className={styles.strikePrice}>
                        {renderCurrency("sub", 10)}
                        {formatIndianPrice(original.toFixed(2))}
                      </span>
                    ) : null}
                  </div>
                  <p className={styles.finalPrice}>
                    {renderCurrency("main", 18)}
                    {formatIndianPrice(total.toFixed(2))}
                  </p>
                </div>

                {!activities.isSpecialActivity ? (
                  <div className={styles.actionButtons}>
                    <button
                      type="button"
                      className={styles.addCartBtn}
                      disabled={cartLoading || bookingLoading}
                      onClick={() => {
                        if (cartLoading || bookingLoading) return;
                        if (activities.isAnniversaryActivity && !userId) {
                          openAuthModal({
                            mode: "signup",
                            returnUrl: router.asPath,
                          });
                          return;
                        }
                        setBookingIndex(index);
                        handleAddToCart(pkg);
                      }}
                      aria-label="Add to cart"
                    >
                      {cartLoading && bookingIndex === index
                        ? isDesktop
                          ? "Adding..."
                          : <IoCart size={20} />
                        : isDesktop
                          ? "Add to cart"
                          : <IoCart size={20} />}
                    </button>
                    <button
                      type="button"
                      className={`${styles.bookNowBtn} ${!isBookingAvailable || cartLoading || bookingLoading ? styles.bookNowDisabled : ""}`}
                      id="activity-book-now-section"
                      disabled={!isBookingAvailable || cartLoading || bookingLoading}
                      onClick={() => {
                        if (cartLoading || bookingLoading) return;
                        if (activities.isAnniversaryActivity && !userId) {
                          openAuthModal({
                            mode: "signup",
                            returnUrl: router.asPath,
                          });
                          return;
                        }
                        setBookingIndex(index);
                        handleAddToCart(pkg, "booking");
                      }}
                    >
                      {bookingLoading && bookingIndex === index
                        ? "Booking..."
                        : isDesktop
                          ? "Book Now"
                          : "Select"}
                    </button>
                  </div>
                ) : (
                  <div className={styles.actionButtons}>
                    <button
                      type="button"
                      className={styles.addCartBtn}
                      onClick={() => setShowEnquiryForm(true)}
                    >
                      Send Enquiry
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    });

  const truncated = getTruncatedHTML(highlightsDesc, 65);

  return (
    <div className={styles.root}>
      <div
        className={`${styles.includesBox} ${hideIncludesOnMobile ? styles.includesBoxHiddenMobile : ""}`}
      >
        <h2 className={styles.includesTitle}>
          Included in This Activity Package
        </h2>
        <div className={styles.includesGrid}>
          {items.length > 0 ? (
            items.map((item, index) => (
              <div key={`${item.label}-${index}`} className={styles.includeItem}>
                <Image
                  src={item.icon}
                  alt={item.label}
                  width={15}
                  height={15}
                  className={styles.includeIcon}
                  unoptimized
                />
                <span>{item.label}</span>
              </div>
            ))
          ) : (
            <p className={styles.noData}>No inclusion data available</p>
          )}
        </div>
      </div>

      <div className={styles.tabStickyWrap}>
        <div ref={tabSentinelRef} className={styles.tabSentinel} aria-hidden="true" />
        {tabsPinned && tabsHeight ? (
          <div
            className={styles.tabPlaceholder}
            style={{ height: tabsHeight }}
            aria-hidden="true"
          />
        ) : null}
        <div
          ref={tabsRef}
          className={`${styles.tabContainer} ${isDesktop ? styles.tabContainerSticky : ""} ${tabsPinned ? styles.tabContainerPinned : ""}`}
        >
          {tabs.map((tab, index) => (
            <button
              key={tab}
              type="button"
              className={`${styles.tabButton} ${activeTab === index ? styles.tabButtonActive : ""}`}
              onClick={() => scrollToSection(index)}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      <section ref={highlightsRef} className={styles.sectionCard}>
        <button
          type="button"
          className={styles.sectionHeader}
          onClick={() => setHighlightsOpen((v) => !v)}
        >
          <span className={styles.accentBar} />
          <h2 className={styles.sectionTitle}>Highlights</h2>
          <IoChevronDown className={highlightsOpen ? styles.chevronOpen : ""} />
        </button>
        {highlightsOpen ? (
          <div className={styles.sectionBody}>
            {highlightsDesc ? (
              <div className={styles.highlightText}>
                {showFullHighlights ? (
                  <>
                    <span dangerouslySetInnerHTML={{ __html: highlightsDesc }} />{" "}
                    {truncated.isTruncated ? (
                      <button
                        type="button"
                        className={styles.readMore}
                        onClick={() => setShowFullHighlights(false)}
                      >
                        Read less
                      </button>
                    ) : null}
                  </>
                ) : (
                  <p>
                    {truncated.text}
                    {truncated.isTruncated ? (
                      <>
                        ...{" "}
                        <button
                          type="button"
                          className={styles.readMore}
                          onClick={() => setShowFullHighlights(true)}
                        >
                          Read more
                        </button>
                      </>
                    ) : null}
                  </p>
                )}
              </div>
            ) : (
              <p className={styles.noData}>No highlights data available</p>
            )}
          </div>
        ) : null}
      </section>

      <section
        ref={selectPackageRefResolved}
        id="select-package"
        className={styles.sectionCard}
      >
        <div className={styles.packageHeader}>
          <div className={styles.sectionHeaderStatic}>
            <span className={styles.accentBar} />
            <h2 className={styles.sectionTitle}>Select Package</h2>
          </div>
          <div className={styles.toggle}>
            <button
              type="button"
              className={`${styles.toggleOption} ${packageType === "standard" ? styles.toggleOptionActive : ""}`}
              onClick={() => setPackageType("standard")}
            >
              Standard
            </button>
            {comboOptions.length > 0 ? (
              <button
                type="button"
                className={`${styles.toggleOption} ${packageType === "combo" ? styles.toggleOptionActive : ""}`}
                onClick={() => setPackageType("combo")}
              >
                Combo Deals
              </button>
            ) : null}
          </div>
        </div>

        <div>
          <div className={styles.controlsRow}>
            <div className={`${styles.field} traveler-dropdown-container`}>
              <label className={styles.fieldLabel}>Traveler</label>
              <button
                type="button"
                className={styles.selectInput}
                onClick={() => setShowTravelerDropdown((v) => !v)}
              >
                <span>{getTravelerDisplayText()}</span>

                <IoChevronDown
                  className={styles.inputSvg}
                />
              </button>

              {showTravelerDropdown ? (
                <div className={styles.travelerMenu}>
                  <div className={styles.travelerRow}>
                    <span>Adult</span>
                    <div className={styles.counter}>
                      <button
                        type="button"
                        onClick={() =>
                          handleTravelerChange("adults", "decrement")
                        }
                        disabled={selectedTravelers.adults <= 1}
                      >
                        −
                      </button>
                      <span>{selectedTravelers.adults}</span>
                      <button
                        type="button"
                        disabled={
                          selectedTravelers.adults === 2 &&
                          activities.isAnniversaryActivity
                        }
                        onClick={() =>
                          handleTravelerChange("adults", "increment")
                        }
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {bookingRules.childNotAllowed &&
                    !activities.isAnniversaryActivity ? (
                    <div className={styles.travelerRow}>
                      <div>
                        <div>Children</div>
                        <small>{bookingRules.childAge || "3-11 Yrs"}</small>
                      </div>
                      <div className={styles.counter}>
                        <button
                          type="button"
                          onClick={() =>
                            handleTravelerChange("children", "decrement")
                          }
                          disabled={selectedTravelers.children <= 0}
                        >
                          −
                        </button>
                        <span>{selectedTravelers.children}</span>
                        <button
                          type="button"
                          onClick={() =>
                            handleTravelerChange("children", "increment")
                          }
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {bookingRules.infantNotAllowed &&
                    !activities.isAnniversaryActivity ? (
                    <div className={styles.travelerRow}>
                      <div>
                        <div>Infants</div>
                        <small>{bookingRules.infantAge || "0-2 Yrs"}</small>
                      </div>
                      <div className={styles.counter}>
                        <button
                          type="button"
                          onClick={() =>
                            handleTravelerChange("infants", "decrement")
                          }
                          disabled={selectedTravelers.infants <= 0}
                        >
                          −
                        </button>
                        <span>{selectedTravelers.infants}</span>
                        <button
                          type="button"
                          onClick={() =>
                            handleTravelerChange("infants", "increment")
                          }
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {activities.isAnniversaryActivity && unlockFree ? (
                <div
                  className={`${styles.freeBeesMessage} ${styles.freeBeesSuccess}`}
                >
                  Congrats! 🎉 Unlocked a Free Traveler
                </div>
              ) : activities.isAnniversaryActivity ? (
                <div className={styles.freeBeesMessage}>
                  Add One More Traveler, Enjoy One Free!
                </div>
              ) : null}
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Select date</label>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={travelDate ? dayjs(travelDate) : null}
                  minDate={dayjs().add(1, "day")}
                  format="DD/MM/YYYY"
                  onChange={(date) => {
                    setTravelDate(date ? date.toDate() : null);
                    setSelectedTime("");
                    if (date) checkBookingAvailability(date.toDate(), "");
                  }}
                  views={["year", "month", "day"]}
                  openTo="day"
                  slotProps={{ textField: { fullWidth: true } }}
                  slots={{
                    openPickerIcon: () => (
                      <img
                        src="/calender-icon.svg"
                        alt="calendar"
                        className={styles.calendarIcon}
                      />
                    ),
                  }}
                  sx={{
                    "& .MuiPickersSectionList-root": {
                      padding: "16px 10px 12px 0",
                      fontSize: "14px",
                    },
                  }}
                />
              </LocalizationProvider>
              <p className={styles.dateHint}>*Check Dates before booking</p>
            </div>
          </div>

          {show ? (
            <div className={styles.confettiOverlay}>
              <span>🎉</span>
            </div>
          ) : null}

          {!isBookingAvailable && bookingMessage ? (
            <div className={styles.warning}>{bookingMessage}</div>
          ) : null}

          <button
            type="button"
            className={styles.availableToggle}
            onClick={() => setIsOpen((v) => !v)}
          >
            <span>
              {packageType === "standard"
                ? (() => {
                  const filteredCount =
                    getFilteredPackagesByAvailability(standardOptions).length;
                  return filteredCount > 0
                    ? `${filteredCount} types of packages`
                    : "No Standard Activities Available for Selected Date/Time";
                })()
                : comboOptions.length > 0
                  ? (() => {
                    const filteredCount =
                      getFilteredPackagesByAvailability(comboOptions).length;
                    return filteredCount > 0
                      ? `${filteredCount} combo offers`
                      : "No Combo Offers Available for Selected Date/Time";
                  })()
                  : "No Activities Available"}
            </span>
            <IoChevronDown className={isOpen ? styles.chevronOpen : ""} />
          </button>

          {isOpen ? (
            <div className={styles.packageList}>
              {packageType === "standard" ? (
                (() => {
                  const filteredStandardOptions =
                    getFilteredPackagesByAvailability(standardOptions);
                  return filteredStandardOptions.length > 0 ? (
                    renderPackageOptions(filteredStandardOptions, "standard")
                  ) : (
                    <p className={styles.noData}>
                      {travelDate || selectedTime
                        ? "No standard activities available for selected date"
                        : "No standard activities available"}
                    </p>
                  );
                })()
              ) : comboOptions.length > 0 ? (
                (() => {
                  const filteredComboOptions =
                    getFilteredPackagesByAvailability(comboOptions);
                  return filteredComboOptions.length > 0 ? (
                    renderPackageOptions(filteredComboOptions, "combo")
                  ) : (
                    <p className={styles.noData}>
                      {travelDate || selectedTime
                        ? "No combo deals available for selected date/time"
                        : "No combo deals available"}
                    </p>
                  );
                })()
              ) : (
                <p className={styles.noData}>No combo deals available</p>
              )}
            </div>
          ) : null}
        </div>
      </section>

      {/*ACTIVITIES Inclusion / Exclusion */}
      <section ref={inclusionExclusionRef} className={styles.sectionCard}>
        <button
          type="button"
          className={styles.sectionHeader}
          onClick={() => setInclusionOpen((v) => !v)}
        >
          <span className={styles.accentBar} />
          <h2 className={styles.sectionTitle}>Inclusions & Exclusions</h2>
          <IoChevronDown className={inclusionOpen ? styles.chevronOpen : ""} />
        </button>
        {
          inclusionOpen ? (
            <div className={styles.sectionBody}>
              {inclusions.length || exclusions.length ? (
                <div className={styles.ieGrid}>
                  <div className={styles.ieCard}>
                    <h3>Inclusions</h3>
                    <ul>
                      {inclusions.map((item, index) => (
                        <li key={`inc-${index}`}>
                          <span className={`${styles.ieIcon} ${styles.ieYes}`}>
                            <FaCheck size={10} />
                          </span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className={styles.ieCard}>
                    <h3>Exclusions</h3>
                    <ul>
                      {exclusions.length ? (
                        exclusions.map((item, index) => (
                          <li key={`exc-${index}`}>
                            <span className={`${styles.ieIcon} ${styles.ieNo}`}>
                              <FaTimes size={10} />
                            </span>
                            <span>{item}</span>
                          </li>
                        ))
                      ) : (
                        <li>
                          <span className={`${styles.ieIcon} ${styles.ieNo}`}>
                            <FaTimes size={10} />
                          </span>
                          <span>None listed</span>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              ) : (
                <p className={styles.noData}>No inclusion/exclusion data available</p>
              )}
            </div>
          ) : null
        }
      </section>

      {showEnquiryForm ? (
        <div className={styles.enquiryOverlay}>
          <div className={styles.enquiryModal}>
            <p>Send Enquiry for {activities.activityName}</p>
            <button type="button" onClick={() => setShowEnquiryForm(false)}>
              Close
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
